import { Outlet } from 'react-router-dom';

import { lazyWithRetry } from 'src/utils/lazy-with-retry';

import { AuthGuard } from 'src/auth/guard';
import ErrorView from 'src/online-payment/components/ErrorView';
import PointPayUserDataGuard from 'src/online-payment/auth/user-data-guard';
import { ErrorBoundary } from 'src/online-payment/components/ErrorBoundary';
import OnlinePaymentLayout from 'src/online-payment/layouts/OnlinePaymentLayout';
import PayformsAccessGuard from 'src/online-payment/components/payforms/PayformsAccessGuard';

import { ChunkErrorBoundary } from 'src/components/error-boundary/ChunkErrorBoundary';
// ----------------------------------------------------------------------
const DashboardPage = lazyWithRetry(() => import('src/online-payment/pages/dashboard'));
const TransactionsPage = lazyWithRetry(() => import('src/online-payment/pages/transactions'));
const TransactionDetailPage = lazyWithRetry(
  () => import('src/online-payment/pages/transactions/detail')
);
const CustomersPage = lazyWithRetry(() => import('src/online-payment/pages/customers'));
const CustomerDetailPage = lazyWithRetry(() => import('src/online-payment/pages/customers/detail'));
const PaymentDetailPage = lazyWithRetry(
  () => import('src/online-payment/pages/transactions/payment-detail')
);
const LegacyCustomerRedirect = lazyWithRetry(
  () => import('src/online-payment/pages/customers/legacy-redirect')
);
const SettingsPage = lazyWithRetry(() => import('src/online-payment/pages/settings'));
const PaymentLinksPage = lazyWithRetry(() => import('src/online-payment/pages/payment-links'));
// Payforms (Public Experiences, merchant self-service). Business context is the
// merchant session's business; navigation/controls are gated by usePayformsAccess
// (hide/show only) and every route is guarded here by capability.
const PayformsListPage = lazyWithRetry(() => import('src/online-payment/pages/payforms'));
const PayformsNewPage = lazyWithRetry(() => import('src/online-payment/pages/payforms/new'));
const PayformsDetailPage = lazyWithRetry(() => import('src/online-payment/pages/payforms/detail'));
const PayformsSubmissionsPage = lazyWithRetry(
  () => import('src/online-payment/pages/payforms/submissions')
);

// Common guard wrapper for both dashboard and v3 routes
const CommonGuardWrapper = ({ children }: { children: React.ReactNode }) => (
  <AuthGuard type="dashboard">
    <PointPayUserDataGuard>{children}</PointPayUserDataGuard>
  </AuthGuard>
);
export const pointPayRoutes = [
  {
    path: 'online-payment',
    element: (
      <CommonGuardWrapper>
        <ErrorBoundary fallback={<ErrorView />}>
          <OnlinePaymentLayout>
            <ChunkErrorBoundary>
              <Outlet />
            </ChunkErrorBoundary>
          </OnlinePaymentLayout>
        </ErrorBoundary>
      </CommonGuardWrapper>
    ),
    children: [
      { index: true, element: <DashboardPage /> },
      // { path: 'dashboard', element: <DashboardPage /> },
      { path: 'transactions', element: <TransactionsPage /> },
      { path: 'transactions/:saleId', element: <TransactionDetailPage /> },
      { path: 'payments', element: <TransactionsPage /> },
      { path: 'payments/:paymentId', element: <PaymentDetailPage /> },
      { path: 'payment-links', element: <PaymentLinksPage /> },
      {
        path: 'customers',
        element: <CustomersPage />,
      },
      { path: 'customers/:customerId', element: <CustomerDetailPage /> },
      { path: 'settings', element: <SettingsPage /> },
      // Payforms. Static segments (`new`, `customers`) are declared before the
      // `:experienceId` parameter and React Router ranks static segments
      // higher anyway, so `/payforms/customers` can never be read as an id.
      {
        path: 'payforms',
        element: (
          <PayformsAccessGuard capability="VIEW">
            <PayformsListPage />
          </PayformsAccessGuard>
        ),
      },
      {
        path: 'payforms/new',
        element: (
          <PayformsAccessGuard capability="MANAGE">
            <PayformsNewPage />
          </PayformsAccessGuard>
        ),
      },
      {
        path: 'payforms/customers',
        element: <LegacyCustomerRedirect />,
      },
      {
        path: 'payforms/customers/:customerId',
        element: <LegacyCustomerRedirect />,
      },
      {
        path: 'payforms/:experienceId',
        element: (
          <PayformsAccessGuard capability="VIEW">
            <PayformsDetailPage />
          </PayformsAccessGuard>
        ),
      },
      {
        path: 'payforms/:experienceId/submissions',
        element: (
          <PayformsAccessGuard capability="PII">
            <PayformsSubmissionsPage />
          </PayformsAccessGuard>
        ),
      },
    ],
  },
];
